'use client';
import { useBreadcrumbs } from '@repo/ui/src/context/BreadCrumbProvider';
import React, { useEffect } from 'react';
import dynamic from 'next/dynamic';
import Image from '@repo/ui/src/components/Image/New';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import useWindowSize from '@repo/ui/src/hooks/useWindowSize';
import { classNames } from '@repo/ui/src/utils/classNames';
import TierTwoHeader from './_TierTwoHeading';
import HeroLogos from './_HeroLogos';
import { MediaType } from '@repo/ui/src/types/image';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { LinkProps } from '@repo/ui/src/types/link';
import { HeaderVariationProps } from '@repo/ui/src/types/headerVariation';
import SecondaryCta from '@repo/ui/src/components/TierTwoHero/_SecondaryCta';
import { YoutubeLinkProps } from '@repo/ui/src/types/youtubeLink';
import { MetaProps } from '@repo/ui/src/types/meta';
import { useIsMobile } from '@repo/ui/src/utils/useMediaQuery';

const IMAGE_META_SCHEMA = 'http://bigcontent.io/cms/schema/v1/core#/definitions/image-link';
const VIDEO_META_SCHEMA = 'http://bigcontent.io/cms/schema/v1/core#/definitions/video-link';
const FULL_TO_THEATER = 1920;

const BackgroundVideo = dynamic(() => import('@repo/ui/src/components/BackgroundVideo'), {
  ssr: true,
  loading: () => <div className="w-full h-full bg-neutral-900 grid-in-a" />,
});

const heroPadding = {
  none: 'md:py-0',
  small: 'md:py-20',
  medium: 'md:py-28',
  large: 'md:py-36',
};

interface Props {
  title?: string;
  description?: string;
  logoMaxHeight?: string;
  logoMaxWidth?: string;
  _testID?: string;
  opacityOverride?: boolean;
  logos?: Array<MediaType>;
  subtitle?: string;
  searchPlaceHolder?: string;
  showDivider?: boolean;
  searchBy?: string;
  navLinks?: Array<{
    link: LinkProps;
  }>;
  backgroundImage: MediaType;
  link?: LinkProps;
  cta?: Array<LinkProps>;
  headerVariation: HeaderVariationProps;
  opacityLayerClass?: string;
  fitToText?: boolean;
  youtubeLink?: YoutubeLinkProps;
  youtubestring?: string;
  _meta: MetaProps;
  children?: React.ReactNode;
  padding: 'small' | 'medium' | 'large' | 'none';
  videoOverlay?: boolean;
}

const TierTwoHero = ({
  backgroundImage,
  opacityLayerClass,
  _testID,
  opacityOverride = false,
  title,
  logos,
  subtitle,
  headerVariation,
  searchPlaceHolder,
  logoMaxHeight,
  logoMaxWidth,
  fitToText,
  description,
  link,
  navLinks,
  youtubeLink,
  youtubestring,
  _meta,
  cta,
  children,
  padding,
  videoOverlay,
}: Props) => {
  const { setDarkBackground } = useBreadcrumbs();
  const hasVideo = backgroundImage && backgroundImage._meta?.schema === VIDEO_META_SCHEMA;
  const subtitleAsDesc =
    _meta?.schema === 'https://traegergrills.com/data/contentTypes/generalHeroBannerWithText.json';

  const hasCta = (youtubeLink && youtubestring) || link?.displayName || cta;
  useEffect(() => {
    if (backgroundImage) {
      setDarkBackground(true);
    }
    return () => setDarkBackground(false);
  }, [backgroundImage, setDarkBackground]);

  const { width = 1920 } = useWindowSize(); // Default to common width for SSR consistency
  const xsOnly = useIsMobile();

  let imgUrl;
  let isVideo;

  if (backgroundImage?._meta?.schema === IMAGE_META_SCHEMA) {
    isVideo = false;
    imgUrl = buildImageSrc(backgroundImage, width, xsOnly ? width : 2, xsOnly ? 450 : 1);
  } else if (backgroundImage?._meta?.schema === VIDEO_META_SCHEMA) {
    isVideo = true;
    // Videos will use default 16:9 aspect ratio via aspect-video
  }

  const givenHeightStyle = opacityLayerClass === 'given-height' && !isVideo;
  const blackOverlay = imgUrl || (isVideo && !opacityOverride);

  const maxHeight = logos || searchPlaceHolder;
  const hasNavLinks = navLinks?.[0]?.link?.displayName;

  return (
    <div
      className={classNames('relative flex md:max-h-screen', givenHeightStyle && 'h-full max-h-full')}
      data-test-id={_testID ? `hero_banner_${_testID}` : undefined}
      data-testid={_testID}
    >
      {imgUrl && <Image src={imgUrl} alt="" fill className="object-cover z-0" priority />}
      <div
        className={classNames(
          'w-full grid grid-rows-1 items-center',
          !opacityOverride && 'z-20',
          backgroundImage && blackOverlay ? 'bg-black bg-opacity-60' : 'bg-[#f6f4f3]',
          // Video: Use aspect ratio for consistent sizing, but allow content overflow on mobile
          isVideo && ['md:aspect-video md:max-h-screen md:overflow-y-hidden grid-areas-overlap', 'min-h-[400px]'],
          // Non-video: Allow natural sizing with padding and flexible height
          !isVideo && [
            'min-h-[400px] md:max-h-screen overflow-y-hidden [box-sizing:content-box]',
            padding && heroPadding[padding],
            !givenHeightStyle && maxHeight && 'md:min-h-[580px]',
          ],
        )}
      >
        {!children && (
          <div
            className={classNames(
              'grid h-full',
              isVideo && 'grid-in-a z-10',
              videoOverlay && 'bg-black bg-opacity-60',
            )}
            data-testid="video-overlay"
          >
            <div
              className={classNames(
                '!text-center flex flex-col justify-center align-center items-center w-full sm:max-w-paragraph mx-auto p-4 md:py-20 gap-6',
                fitToText && 'pb-4 md:pb-5 md:pt-4',
                backgroundImage ? 'text-white' : 'text-black',
              )}
            >
              <TierTwoHeader
                subtitleAsDesc={subtitleAsDesc}
                subtitle={subtitle}
                title={title}
                headerVariation={headerVariation}
                description={description}
                darkMode={!!backgroundImage}
              />
              {logos && (
                <HeroLogos
                  logos={logos}
                  logoMaxHeight={logoMaxHeight}
                  logoMaxWidth={logoMaxWidth}
                  hasVideo={hasVideo}
                />
              )}
              {hasCta && (
                <SecondaryCta
                  youtubeLink={youtubeLink}
                  youtubestring={youtubestring}
                  link={link}
                  cta={cta}
                />
              )}
              {hasNavLinks && (
                <div className="flex flex-wrap justify-center gap-5 sm:flex-row sm:gap-10">
                  {navLinks?.map((link, i) => (
                    <Button as={Anchor} variant={ButtonVariant.skewer} dark {...link?.link} key={i}>
                      {link?.link?.displayName}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        {children}
        {isVideo && (
          <div className="w-full h-full grid-in-a">
            <BackgroundVideo
              video={backgroundImage}
              width={width > FULL_TO_THEATER ? FULL_TO_THEATER : width}
              playButton
              className="w-full h-full object-cover"
              fillContainer
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TierTwoHero;
